import { computeAccessibleName } from 'dom-accessibility-api'

// The screen scan (#1363): every control the user can see, read the way a
// screen reader does — role and accessible name. The Help pane's tour outlines
// (help/outline.ts) and the Tables and Edit Spec coverage tests use it. The
// tools that pressed and set these controls for Douglas were deleted with him
// (Douglas page agent T21).

export type ControlRole =
  | 'button' | 'link' | 'tab' | 'switch' | 'checkbox' | 'radio'
  | 'menuitem' | 'option' | 'combobox' | 'textbox' | 'treeitem' | 'slider'

export interface ScreenControl {
  id: string
  role: ControlRole
  label: string
  region: string
  state?: string
  value?: string
  disabled?: true
  confirm?: true
}

const ROLES = new Set<string>([
  'button', 'link', 'tab', 'switch', 'checkbox', 'radio', 'menuitem', 'menuitemcheckbox',
  'menuitemradio', 'option', 'combobox', 'textbox', 'searchbox', 'treeitem', 'slider', 'spinbutton',
])
const CANDIDATES = [
  'button', 'a[href]', 'input', 'select', 'textarea', '[contenteditable="true"]',
  ...[...ROLES].map((r) => `[role="${r}"]`),
].join(',')

/** Labels of controls that change or send something: a scan marks them `confirm`. */
export const CONFIRM_LABEL =
  /\b(delete|remove|publish|send|run|regenerate|re-generate|sign[ -]?off|stop|revoke|overwrite|discard|archive|purge|evict)\b/i

const LABEL_MAX = 80

/** The role a screen reader would announce, or null for a non-control. */
export function controlRole(el: Element): ControlRole | null {
  const explicit = el.getAttribute('role')
  // A rich-text editor shown read-only keeps role="textbox"; nobody can type there.
  if (explicit === 'textbox' && el.getAttribute('contenteditable') === 'false') return null
  if (explicit && ROLES.has(explicit)) {
    if (explicit.startsWith('menuitem')) return 'menuitem'
    if (explicit === 'searchbox') return 'textbox'
    if (explicit === 'spinbutton') return 'textbox'
    return explicit as ControlRole
  }
  const tag = el.tagName
  if (tag === 'BUTTON') return 'button'
  if (tag === 'A') return el.hasAttribute('href') ? 'link' : null
  if (tag === 'SELECT') return 'combobox'
  if (tag === 'TEXTAREA') return 'textbox'
  if (tag === 'INPUT') {
    const type = (el.getAttribute('type') || 'text').toLowerCase()
    if (type === 'hidden' || type === 'password' || type === 'file') return null
    if (type === 'checkbox') return el.closest('.MuiSwitch-root') ? 'switch' : 'checkbox'
    if (type === 'radio') return 'radio'
    if (type === 'range') return 'slider'
    if (type === 'button' || type === 'submit' || type === 'reset') return 'button'
    return 'textbox'
  }
  if (el.getAttribute('contenteditable') === 'true') return 'textbox'
  return null
}

function isHidden(el: Element): boolean {
  // Modals (MUI Dialog, Menu, Popover) set aria-hidden on everything behind
  // them, so this also limits the list to what the user can actually use.
  if (el.closest('[aria-hidden="true"], [inert], [hidden], [data-voice="off"]')) return true
  const check = (el as HTMLElement & { checkVisibility?: (o?: object) => boolean }).checkVisibility
  if (typeof check === 'function') return !check.call(el, { checkOpacity: false, checkVisibilityCSS: true })
  // jsdom has no layout: walk the computed style instead.
  for (let n: Element | null = el; n; n = n.parentElement) {
    const s = getComputedStyle(n)
    if (s.display === 'none' || s.visibility === 'hidden') return true
  }
  return false
}

function isDisabled(el: Element): boolean {
  return (el as HTMLButtonElement).disabled === true || el.getAttribute('aria-disabled') === 'true' ||
    !!el.closest('fieldset[disabled]')
}

/** Accessible name, falling back to a field's placeholder. */
export function controlLabel(el: Element): string {
  let name = ''
  try {
    name = computeAccessibleName(el)
  } catch {
    name = el.getAttribute('aria-label') || ''
  }
  if (!name) name = el.getAttribute('placeholder') || el.getAttribute('title') || ''
  name = name.replace(/\s+/g, ' ').trim()
  return name.length > LABEL_MAX ? `${name.slice(0, LABEL_MAX - 1)}…` : name
}

function regionOf(el: Element): string {
  const dialog = el.closest('[role="dialog"], [role="alertdialog"]')
  if (dialog) {
    const name = controlLabel(dialog)
    return name ? `dialog: ${name}` : 'dialog'
  }
  if (el.closest('[role="menu"], [role="listbox"]')) return 'menu'
  if (el.closest('header, [role="banner"], .MuiAppBar-root')) return 'header'
  if (el.closest('nav, aside, [role="navigation"], [role="complementary"]')) return 'side panel'
  return 'main'
}

function stateOf(el: Element, role: ControlRole): { state?: string; value?: string } {
  const out: { state?: string; value?: string } = {}
  const bits: string[] = []
  if (role === 'checkbox' || role === 'switch' || role === 'radio') {
    const checked = el instanceof HTMLInputElement ? el.checked : el.getAttribute('aria-checked') === 'true'
    bits.push(checked ? 'on' : 'off')
  }
  if (el.getAttribute('aria-selected') === 'true') bits.push('selected')
  if (el.getAttribute('aria-pressed') === 'true') bits.push('pressed')
  const expanded = el.getAttribute('aria-expanded')
  if (expanded === 'true') bits.push('open')
  else if (expanded === 'false' && role === 'combobox') bits.push('closed')
  if (bits.length) out.state = bits.join(', ')
  if (role === 'textbox' || role === 'combobox' || role === 'slider') {
    let v = ''
    if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) v = el.value
    else if (role === 'combobox' || el.getAttribute('contenteditable') === 'true') v = (el.textContent || '').trim()
    if (v) out.value = v.length > 60 ? `${v.slice(0, 59)}…` : v
  }
  return out
}

function needsConfirm(el: Element, label: string): boolean {
  if (el.closest('[data-voice="confirm"]') || CONFIRM_LABEL.test(label)) return true
  // A dialog's own buttons are often a bare "Confirm" or "OK" whose meaning is
  // the dialog's title ("Delete pipeline?"). Cancel and Close stay free.
  const dialog = el.closest('[role="dialog"], [role="alertdialog"]')
  return !!dialog && !/^(cancel|close|no|back|keep)\b/i.test(label) && CONFIRM_LABEL.test(controlLabel(dialog))
}

interface Scanned {
  el: Element
  control: ScreenControl
}

/** Every visible control, in document order. Unlabelled ones have label ''. */
export function scanControls(root: ParentNode = document): Scanned[] {
  const out: Scanned[] = []
  const seen = new Set<Element>()
  root.querySelectorAll(CANDIDATES).forEach((el) => {
    if (seen.has(el)) return
    seen.add(el)
    const role = controlRole(el)
    if (!role || isHidden(el)) return
    const label = controlLabel(el)
    const control: ScreenControl = { id: '', role, label, region: regionOf(el), ...stateOf(el, role) }
    if (isDisabled(el)) control.disabled = true
    if (needsConfirm(el, label)) control.confirm = true
    out.push({ el, control })
  })
  return out
}
